import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  HardHat,
  FileText,
  Users,
  Truck,
  Briefcase,
  Receipt,
  UserCircle,
  Brain,
  LogOut,
  Building2,
  ClipboardList,
  ShieldAlert,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import ChatSheet from "@/components/ChatSheet";
import SearchDialog from "@/components/SearchDialog";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "dashboard" },
  { to: "/projects", label: "Projects", icon: HardHat, tid: "projects" },
  { to: "/documents", label: "Documents", icon: FileText, tid: "documents" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardList, tid: "work-orders" },
  { to: "/vendors", label: "Vendors", icon: Briefcase, tid: "vendors" },
  { to: "/machinery", label: "Machinery", icon: Truck, tid: "machinery" },
  { to: "/employees", label: "HR / Employees", icon: Users, tid: "employees" },
  { to: "/invoices", label: "Invoices & Accounts", icon: Receipt, tid: "invoices" },
  { to: "/compliance", label: "Compliance", icon: ShieldAlert, tid: "compliance" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global cmd/ctrl+K shortcut
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="flex min-h-screen bg-zinc-100">
      {/* Sidebar */}
      <aside
        className="sidebar-dark fixed left-0 top-0 h-screen w-64 flex flex-col bg-[#0A0A0A] text-zinc-300 z-30"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-6 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Building2 className="text-orange-500 h-6 w-6" strokeWidth={2.4} />
            <div>
              <div className="font-heading font-black text-white text-[15px] leading-tight tracking-tight">
                MASCOT
              </div>
              <div className="overline text-zinc-500 mt-0.5">Construction • Second Brain</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-0.5">
          {NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`sidebar-nav-${it.tid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-orange-600 text-white font-medium"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <it.icon className="h-4 w-4" strokeWidth={2} />
              {it.label}
            </NavLink>
          ))}

          <div className="pt-4 mt-4 border-t border-zinc-800">
            <button
              data-testid="sidebar-open-chat"
              onClick={() => setChatOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm text-zinc-300 hover:bg-orange-600 hover:text-white transition-colors group"
            >
              <Brain className="h-4 w-4 text-orange-500 group-hover:text-white" strokeWidth={2} />
              <span className="flex-1 text-left">Second Brain AI</span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 border border-zinc-700 group-hover:border-white/40 rounded-sm">
                ASK
              </span>
            </button>
          </div>
        </nav>

        <div className="border-t border-zinc-800 px-3 py-4">
          <div className="flex items-center gap-3 px-2 pb-3">
            <div className="h-8 w-8 rounded-sm bg-orange-600 flex items-center justify-center text-white font-heading font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="overline text-zinc-500 truncate">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="sidebar-logout-btn"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            variant="ghost"
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm h-9 text-sm"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64">
        <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 h-14 flex items-center justify-between px-8">
          <div className="flex items-center gap-3">
            <div className="overline">Mascot Construction Co. — Internal Dashboard</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="header-search-btn"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 text-xs font-medium px-3 py-1.5 border border-zinc-300 rounded-sm hover:border-orange-600 hover:text-orange-600 transition-colors"
            >
              <Search className="h-3.5 w-3.5" />
              Search docs
              <span className="ml-1 font-mono text-[9px] px-1 py-0.5 border border-zinc-300 rounded-sm">⌘K</span>
            </button>
            <button
              data-testid="header-ask-brain-btn"
              onClick={() => setChatOpen(true)}
              className="flex items-center gap-2 text-xs font-medium px-3 py-1.5 border border-zinc-300 rounded-sm hover:border-orange-600 hover:text-orange-600 transition-colors"
            >
              <Brain className="h-3.5 w-3.5" /> Ask Second Brain
            </button>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <UserCircle className="h-4 w-4" />
              <span>{user?.email}</span>
            </div>
          </div>
        </header>

        <main className="p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>

      <ChatSheet open={chatOpen} onOpenChange={setChatOpen} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
